import {
  AHText,
  Box,
  Card,
  Input,
  TodoCategoryIconType,
  useLayoutContext,
} from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IconBar } from "./IconBar";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TodoCategoryEditGeneralScreenMutation } from "./__generated__/TodoCategoryEditGeneralScreenMutation.graphql";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { TodoCategoryEditGeneralScreenQuery } from "./__generated__/TodoCategoryEditGeneralScreenQuery.graphql";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type TodoCategoryEditGeneralScreenProps =
  StoreAdminNavigationScreenProps<"TodoCategoryEditGeneral">;

export function TodoCategoryEditGeneralScreen({
  navigation,
  route,
}: TodoCategoryEditGeneralScreenProps) {
  const data = useLazyLoadQuery<TodoCategoryEditGeneralScreenQuery>(
    graphql`
      query TodoCategoryEditGeneralScreenQuery($id: ID!) {
        todoCategory(id: $id) {
          id
          title
          icon
          isDefault
        }
      }
    `,
    { id: route.params.id }
  );

  const { space } = useLayoutContext();

  const remoteIcon = data.todoCategory.icon ?? "Check";
  const remoteTitle = data.todoCategory.title ?? "";

  const [icon, setIcon] = useState<TodoCategoryIconType>(remoteIcon);
  const [title, setTitle] = useState(remoteTitle);

  const isDirty = useMemo(() => {
    return icon !== remoteIcon || title !== remoteTitle;
  }, [icon, remoteIcon, title, remoteTitle]);

  useEffect(() => {
    setIcon(remoteIcon);
  }, [remoteIcon]);

  useEffect(() => {
    setTitle(remoteTitle);
  }, [remoteTitle]);

  const [update, loading] = useMutation<TodoCategoryEditGeneralScreenMutation>(
    graphql`
      mutation TodoCategoryEditGeneralScreenMutation(
        $id: ID!
        $title: String!
        $icon: TodoCategoryIcon!
      ) {
        todoCategory(id: $id) {
          titleUpdate(title: $title) {
            id
          }
        }
        todoCategory(id: $id) {
          iconUpdate(icon: $icon) {
            id
          }
        }
      }
    `
  );

  const onUpdate = useCallback(() => {
    update({
      variables: {
        title,
        icon,
        id: route.params.id,
      },
      onCompleted: navigation.goBack,
    });
  }, [update, title, icon, route.params.id, navigation.goBack]);

  useNavigationHeader({
    type: "SAVE",
    onCancel: navigation.goBack,
    onSave: onUpdate,
    saveLoading: loading,
    disabled: !isDirty,

    defaultContentStyle: true,
  });
  const intl = useIntl();
  return (
    <KeyboardAwareScrollView
      contentInsetAdjustmentBehavior="automatic"
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{
        padding: space.medium,
        paddingVertical: space.medium,
      }}
    >
      <Box spaceVertical="small">
        <AHText spaceBottom="small" spaceHorizontal="xsmall" type="title-small">
          <FormattedMessage
            defaultMessage="Name"
            description="StoreAdminTodoCategoriesCreate: Name Header"
          />
        </AHText>
        <Input
          autoCapitalize="words"
          placeholder={intl.formatMessage({
            defaultMessage: "Category name",
            description: "StoreAdminTodoCategoriesCreate: Name placeholder",
          })}
          value={title}
          onChangeText={setTitle}
          autoFocus
        />
      </Box>
      {data.todoCategory.isDefault ? null : (
        <Box spaceVertical="small" spaceBottom="xxlarge">
          <AHText
            spaceBottom="small"
            type="title-small"
            spaceHorizontal="xsmall"
          >
            <FormattedMessage
              defaultMessage="Icon"
              description="StoreAdminTodoCategoriesCreate: Icon Header"
            />
          </AHText>
          <Card defaultBorder>
            <IconBar selectedIcon={icon} setSelectedIcon={setIcon} />
          </Card>
        </Box>
      )}
    </KeyboardAwareScrollView>
  );
}
